import React, { useState, useEffect } from 'react';
import dataService from './services/dataService';
import WeekDetailModal from './components/WeekDetailModal';
import { getStatusStyle } from './statusStyles';
import { MoreVertical, Edit2, Trash2 } from 'lucide-react';

const TraumaSurgeryPlannerRefactored = () => {
  const [baseStaff, setBaseStaff] = useState(20);
  const [leave, setLeave] = useState([]);
  const [swaps, setSwaps] = useState({});
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedSurgeon, setSelectedSurgeon] = useState('');
  const [selectedSwapSurgeon1, setSelectedSwapSurgeon1] = useState('');
  const [selectedSwapSurgeon2, setSelectedSwapSurgeon2] = useState('');
  const [selectedLeaveReason, setSelectedLeaveReason] = useState('');
  const [leaveStartDate, setLeaveStartDate] = useState('');
  const [leaveEndDate, setLeaveEndDate] = useState('');
  const [packhamClarkeOverrides, setPackhamClarkeOverrides] = useState({});
  const [bakerBickOverrides, setBakerBickOverrides] = useState({});
  // Off-on-call: consultants taken off the on-call rota from a date (e.g. pre-maternity),
  // still present for daytime work. offOnCall = [{name, fromDate}]; cover per date is an
  // ADDITIONAL shift for the named consultant. offOnCallCover = { 'YYYY-MM-DD': coverName }.
  const [offOnCall, setOffOnCall] = useState([]);
  const [offOnCallCover, setOffOnCallCover] = useState({});
  const [offOnCallPerson, setOffOnCallPerson] = useState('');
  const [offOnCallFrom, setOffOnCallFrom] = useState('');
  const [offOnCallTo, setOffOnCallTo] = useState('');
  const [coverDate, setCoverDate] = useState('');
  const [coverPerson, setCoverPerson] = useState('');
  // Bulk Tuesday rota editor
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  const [bulkRota, setBulkRota] = useState(null); // { 'YYYY-MM-DD': personName } once generated
  const [packhamClarkeDate, setPackhamClarkeDate] = useState('');
  const [packhamClarkePerson, setPackhamClarkePerson] = useState('');
  const [bakerBickDate, setBakerBickDate] = useState('');
  const [bakerBickPerson, setBakerBickPerson] = useState('');
  const [swapDate1, setSwapDate1] = useState('');
  const [swapDate2, setSwapDate2] = useState('');
  const [onCallSingleDate, setOnCallSingleDate] = useState('');
  const [onCallSinglePerson, setOnCallSinglePerson] = useState('');
  const [loading, setLoading] = useState(true);
  const [consultants, setConsultants] = useState([]);
  const [leaveReasons, setLeaveReasons] = useState([]);
  const [rotations, setRotations] = useState({});
  const [weekendRota, setWeekendRota] = useState({});
  const [weekendRotaOverrides, setWeekendRotaOverrides] = useState({});
  const [weekendChangeDate, setWeekendChangeDate] = useState('');
  const [weekendChangeConsultant, setWeekendChangeConsultant] = useState('');
  const [weekendSwapDate1, setWeekendSwapDate1] = useState('');
  const [weekendSwapSurgeon1, setWeekendSwapSurgeon1] = useState('');
  const [weekendSwapDate2, setWeekendSwapDate2] = useState('');
  const [weekendSwapSurgeon2, setWeekendSwapSurgeon2] = useState('');
  const [leaveCollapsed, setLeaveCollapsed] = useState(() => {
    try { return localStorage.getItem('trauma-planner-leaveCollapsed') === 'true'; } catch { return false; }
  });
  const [collapsedConsultants, setCollapsedConsultants] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('trauma-planner-collapsedConsultants') || '[]')); } catch { return new Set(); }
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [openSwapDropdown, setOpenSwapDropdown] = useState(null);
  const [editingSwap, setEditingSwap] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [additionalActivities, setAdditionalActivities] = useState({});
  const [additionalPeople, setAdditionalPeople] = useState({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openSwapDropdown && !event.target.closest('.swap-dropdown')) {
        setOpenSwapDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openSwapDropdown]);

  // Set leave dates when week is selected
  useEffect(() => {
    if (selectedWeek) {
      setLeaveStartDate(selectedWeek.days[0].date.toISOString().split('T')[0]);
      setLeaveEndDate(selectedWeek.days[4].date.toISOString().split('T')[0]);
    }
  }, [selectedWeek]);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load static data
        const consultantsData = dataService.getConsultants();
        setConsultants(consultantsData);

        const reasons = dataService.getLeaveReasons();
        setLeaveReasons(reasons);

        const rotationsData = dataService.getRotations();
        setRotations(rotationsData);

        const weekendRotaData = dataService.getWeekendRota();
        setWeekendRota(weekendRotaData);

        // Load dynamic data from Netlify Functions
        const [leaveData, swapsData, overridesData, additionalActivitiesData, additionalPeopleData, weekendRotaOverridesData] = await Promise.all([
          dataService.getLeave(),
          dataService.getSwaps(),
          dataService.getOverrides(),
          dataService.getAdditionalActivities(),
          dataService.getAdditionalPeople(),
          dataService.getWeekendRotaOverrides()
        ]);

        setLeave(leaveData);
        setSwaps(swapsData);
        setPackhamClarkeOverrides(overridesData.packhamClarke || {});
        setBakerBickOverrides(overridesData.bakerBick || {});
        setOffOnCall(overridesData.offOnCall || []);
        setOffOnCallCover(overridesData.offOnCallCover || {});
        setAdditionalActivities(additionalActivitiesData);
        setAdditionalPeople(additionalPeopleData);
        setWeekendRotaOverrides(weekendRotaOverridesData);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // API handlers
  const handleAddLeave = async (leaveEntry) => {
    try {
      const result = await dataService.addLeave(leaveEntry);
      setLeave(prev => [...prev, result.entry]);
      return result;
    } catch (error) {
      console.error('Error adding leave:', error);
    }
  };

  const handleRemoveLeave = async (id) => {
    try {
      await dataService.removeLeave(id);
      setLeave(prev => prev.filter(l => l.id !== id));
    } catch (error) {
      console.error('Error removing leave:', error);
    }
  };

  const handleAddSwap = async (weekDay, swap) => {
    try {
      await dataService.addSwap(weekDay, swap);
      setSwaps(prev => ({ ...prev, [weekDay]: swap }));
    } catch (error) {
      console.error('Error adding swap:', error);
    }
  };

  const handleRemoveSwap = async (weekDay) => {
    try {
      await dataService.removeSwap(weekDay);
      setSwaps(prev => {
        const updated = { ...prev };
        delete updated[weekDay];
        return updated;
      });
    } catch (error) {
      console.error('Error removing swap:', error);
    }
  };

  const handleAddWeekendChange = async (date, consultantInitials) => {
    try {
      await dataService.addWeekendRotaOverride(date, consultantInitials);
      setWeekendRotaOverrides(prev => ({ ...prev, [date]: consultantInitials }));
    } catch (error) {
      console.error('Error adding weekend rota change:', error);
    }
  };

  const handleRemoveWeekendChange = async (date) => {
    try {
      await dataService.removeWeekendRotaOverride(date);
      setWeekendRotaOverrides(prev => {
        const updated = { ...prev };
        delete updated[date];
        return updated;
      });
    } catch (error) {
      console.error('Error removing weekend rota change:', error);
    }
  };

  // Swap the weekend on-call consultants between two weekend dates
  const addWeekendSwap = async () => {
    if (!weekendSwapDate1 || !weekendSwapSurgeon1 || !weekendSwapDate2 || !weekendSwapSurgeon2) {
      alert('Please fill in both dates and both consultants for the swap.');
      return;
    }
    if (weekendSwapDate1 === weekendSwapDate2) {
      alert('Please select two different dates.');
      return;
    }
    try {
      // Mirror the weekday on-call swap: the two named consultants exchange dates.
      // date1 (was surgeon1) becomes surgeon2; date2 (was surgeon2) becomes surgeon1.
      await handleAddWeekendChange(weekendSwapDate1, weekendSwapSurgeon2);
      await handleAddWeekendChange(weekendSwapDate2, weekendSwapSurgeon1);
      setWeekendSwapDate1('');
      setWeekendSwapSurgeon1('');
      setWeekendSwapDate2('');
      setWeekendSwapSurgeon2('');
    } catch (error) {
      console.error('Error swapping weekend on-call:', error);
    }
  };

  const toggleLeaveCollapsed = () => {
    setLeaveCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('trauma-planner-leaveCollapsed', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const toggleConsultantCollapsed = (name) => {
    setCollapsedConsultants(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem('trauma-planner-collapsedConsultants', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  const handleUpdateOverrides = async () => {
    try {
      await dataService.updateOverrides({
        packhamClarke: packhamClarkeOverrides,
        bakerBick: bakerBickOverrides,
        offOnCall,
        offOnCallCover
      });
    } catch (error) {
      console.error('Error updating overrides:', error);
    }
  };

  // Persist the whole overrides blob, taking fresh off-on-call values so we
  // never save stale state right after a setState.
  const saveOverrides = async (nextOffOnCall, nextCover) => {
    try {
      await dataService.updateOverrides({
        packhamClarke: packhamClarkeOverrides,
        bakerBick: bakerBickOverrides,
        offOnCall: nextOffOnCall,
        offOnCallCover: nextCover
      });
    } catch (error) {
      console.error('Error updating off-on-call overrides:', error);
    }
  };

  const addOffOnCall = async (name, fromDate, toDate) => {
    if (!name || !fromDate) return;
    const entry = { name, fromDate };
    if (toDate) entry.toDate = toDate;
    const next = [...offOnCall.filter(o => o.name !== name), entry];
    setOffOnCall(next);
    setOffOnCallPerson('');
    setOffOnCallFrom('');
    setOffOnCallTo('');
    await saveOverrides(next, offOnCallCover);
  };

  const removeOffOnCall = async (name) => {
    const next = offOnCall.filter(o => o.name !== name);
    setOffOnCall(next);
    await saveOverrides(next, offOnCallCover);
  };

  const setOnCallCover = async (dateStr, coverName) => {
    if (!dateStr) return;
    const next = { ...offOnCallCover };
    if (coverName) next[dateStr] = coverName; else delete next[dateStr];
    setOffOnCallCover(next);
    setCoverDate('');
    setCoverPerson('');
    await saveOverrides(offOnCall, next);
  };

  // Is `name` off the on-call rota on the given YYYY-MM-DD date?
  const isOffOnCall = (name, dateStr) => {
    if (!name || !dateStr) return false;
    return offOnCall.some(o => o.name === name && dateStr >= o.fromDate && (!o.toDate || dateStr <= o.toDate));
  };

  const handleAddAdditionalActivity = async (date, activityName) => {
    try {
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      await dataService.addAdditionalActivity(dateKey, activityName);
      setAdditionalActivities(prev => {
        const updated = { ...prev };
        if (!updated[dateKey]) {
          updated[dateKey] = [];
        }
        if (!updated[dateKey].includes(activityName)) {
          updated[dateKey] = [...updated[dateKey], activityName];
        }
        return updated;
      });
    } catch (error) {
      console.error('Error adding additional activity:', error);
    }
  };

  const handleRemoveAdditionalActivity = async (date, activityName) => {
    try {
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      await dataService.removeAdditionalActivity(dateKey, activityName);
      setAdditionalActivities(prev => {
        const updated = { ...prev };
        if (updated[dateKey]) {
          updated[dateKey] = updated[dateKey].filter(name => name !== activityName);
          if (updated[dateKey].length === 0) {
            delete updated[dateKey];
          }
        }
        return updated;
      });
    } catch (error) {
      console.error('Error removing additional activity:', error);
    }
  };

  const handleAddAdditionalPerson = async (date, name) => {
    try {
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      await dataService.addAdditionalPerson(dateKey, name);
      setAdditionalPeople(prev => {
        const updated = { ...prev };
        if (!updated[dateKey]) {
          updated[dateKey] = [];
        }
        if (!updated[dateKey].includes(name)) {
          updated[dateKey] = [...updated[dateKey], name];
        }
        return updated;
      });
    } catch (error) {
      console.error('Error adding additional person:', error);
    }
  };

  const handleRemoveAdditionalPerson = async (date, name) => {
    try {
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      await dataService.removeAdditionalPerson(dateKey, name);
      setAdditionalPeople(prev => {
        const updated = { ...prev };
        if (updated[dateKey]) {
          updated[dateKey] = updated[dateKey].filter(n => n !== name);
          if (updated[dateKey].length === 0) {
            delete updated[dateKey];
          }
        }
        return updated;
      });
    } catch (error) {
      console.error('Error removing additional person:', error);
    }
  };

  const addPackhamClarkeSwap = () => {
    if (packhamClarkeDate && packhamClarkePerson) {
      const selectedDate = new Date(packhamClarkeDate);
      const monday = new Date(selectedDate);
      monday.setDate(selectedDate.getDate() - selectedDate.getDay() + 1);
      const weekKey = monday.toISOString().split('T')[0];

      setPackhamClarkeOverrides({
        ...packhamClarkeOverrides,
        [weekKey]: packhamClarkePerson
      });

      setPackhamClarkeDate('');
      setPackhamClarkePerson('');
      handleUpdateOverrides();
    }
  };

  const addBakerBickSwap = () => {
    if (bakerBickDate && bakerBickPerson) {
      const selectedDate = new Date(bakerBickDate);
      const monday = new Date(selectedDate);
      monday.setDate(selectedDate.getDate() - selectedDate.getDay() + 1);
      const weekKey = monday.toISOString().split('T')[0];

      setBakerBickOverrides({
        ...bakerBickOverrides,
        [weekKey]: bakerBickPerson
      });

      setBakerBickDate('');
      setBakerBickPerson('');
      handleUpdateOverrides();
    }
  };

  const removePackhamClarkeSwap = (weekKey) => {
    const newOverrides = { ...packhamClarkeOverrides };
    delete newOverrides[weekKey];
    setPackhamClarkeOverrides(newOverrides);
    handleUpdateOverrides();
  };

  const removeBakerBickSwap = (weekKey) => {
    const newOverrides = { ...bakerBickOverrides };
    delete newOverrides[weekKey];
    setBakerBickOverrides(newOverrides);
    handleUpdateOverrides();
  };

  // Helper to find week and day index from a date
  const findWeekDay = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();

    // Get Monday of the current week (same as calculateCapacity)
    const currentWeekMonday = new Date(today);
    currentWeekMonday.setDate(today.getDate() - today.getDay() + 1);
    currentWeekMonday.setHours(0, 0, 0, 0);

    // Get Monday of the target date's week
    const targetWeekMonday = new Date(date);
    targetWeekMonday.setDate(date.getDate() - date.getDay() + 1);
    targetWeekMonday.setHours(0, 0, 0, 0);

    // Calculate week offset from current week (same as calculateCapacity uses 'w')
    const diffWeeks = Math.floor((targetWeekMonday - currentWeekMonday) / (7 * 24 * 60 * 60 * 1000));
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;

    return { week: diffWeeks, day: dayOfWeek };
  };

  const addDoubleSwap = async () => {
    if (!swapDate1 || !selectedSwapSurgeon1 || !swapDate2 || !selectedSwapSurgeon2) {
      alert('Please fill in all fields for the swap.');
      return;
    }

    const pos1 = findWeekDay(swapDate1);
    const pos2 = findWeekDay(swapDate2);

    if (pos1.day > 4 || pos2.day > 4) {
      alert('Swaps can only be made for weekdays (Monday-Friday).');
      return;
    }

    try {
      // For double swap: surgeon1 on date1 swaps with surgeon2 on date2
      // Date1: surgeon1 → surgeon2
      // Date2: surgeon2 → surgeon1
      // Use actual dates as keys for persistence across weeks

      const swap1 = {
        originalSurgeon: selectedSwapSurgeon1,
        newSurgeon: selectedSwapSurgeon2
      };
      const swap2 = {
        originalSurgeon: selectedSwapSurgeon2,
        newSurgeon: selectedSwapSurgeon1
      };

      const bothSwaps = {
        [swapDate1]: swap1,
        [swapDate2]: swap2
      };

      // Update local state with BOTH swaps first
      setSwaps(prev => ({
        ...prev,
        ...bothSwaps
      }));

      // Save both swaps atomically in a single backend call
      await dataService.addSwapsBatch(bothSwaps);

      // Clear form
      setSwapDate1('');
      setSelectedSwapSurgeon1('');
      setSwapDate2('');
      setSelectedSwapSurgeon2('');
    } catch (error) {
      console.error('Error adding double swap:', error);
      // Reload swaps from backend if save failed
      const swapsData = await dataService.getSwaps();
      setSwaps(swapsData);
    }
  };

  const addSingleSwap = async () => {
    if (!onCallSingleDate || !onCallSinglePerson) {
      alert('Please select a date and surgeon for the change.');
      return;
    }

    const pos = findWeekDay(onCallSingleDate);
    if (pos.day > 4) {
      alert('Changes can only be made for weekdays (Monday-Friday).');
      return;
    }

    try {
      // Get the week start date
      const date1 = new Date(onCallSingleDate);
      const weekStart1 = new Date(date1);
      weekStart1.setDate(date1.getDate() - date1.getDay() + 1);

      // Get week number
      const weekNum1 = getWeekNumber(weekStart1);

      // Get the original on-call surgeon for this date (raw, without maternity cover)
      const originalSurgeon1 = getOnCallSurgeonRaw(pos.day, weekNum1, weekStart1);

      // Use actual date as key for persistence across weeks
      await handleAddSwap(onCallSingleDate, {
        originalSurgeon: originalSurgeon1,
        newSurgeon: onCallSinglePerson
      });

      // Clear form
      setOnCallSingleDate('');
      setOnCallSinglePerson('');
    } catch (error) {
      console.error('Error adding single swap:', error);
    }
  };

  // Helper functions using loaded data
  const getConsultantNames = () => {
    return consultants.map(c => c.name).sort();
  };

  const getConsultantByInitials = (initials) => {
    const consultant = consultants.find(c => c.initials === initials);
    return consultant ? consultant.name : initials;
  };

  const getInitials = (fullName) => {
    if (!fullName) return '';
    const consultant = consultants.find(c => c.name === fullName);
    return consultant ? consultant.initials : fullName.split(' ').map(n => n[0]).join('');
  };

  // Baker/Bick pattern with override capability
  const getBakerBickPattern = (weekStartDate) => {
    const septemberStart = new Date('2025-09-01');
    const septemberMonday = new Date(septemberStart);
    septemberMonday.setDate(septemberStart.getDate() - septemberStart.getDay() + 1);

    const weekKey = weekStartDate.toISOString().split('T')[0];
    if (bakerBickOverrides[weekKey]) {
      return bakerBickOverrides[weekKey];
    }

    if (weekStartDate < septemberMonday) {
      return 'Rich Baker';
    }

    const weeksSinceSeptember = Math.floor((weekStartDate - septemberMonday) / (7 * 24 * 60 * 60 * 1000));
    const pattern = rotations.bakerBickRotation?.pattern || [];
    return pattern[weeksSinceSeptember % 8] || 'Rich Baker';
  };

  // Packham/Clarke pattern with override capability
  const getPackhamClarkePattern = (weekNumber, weekStartDate) => {
    const weekKey = weekStartDate.toISOString().split('T')[0];
    if (packhamClarkeOverrides[weekKey]) {
      return packhamClarkeOverrides[weekKey];
    }

    // Use the rotation pattern from rotations.json
    const startDate = new Date(rotations.packhamClarkeRotation?.startDate || '2026-01-05');
    const weeksSinceStart = Math.floor((weekStartDate - startDate) / (7 * 24 * 60 * 60 * 1000));
    const pattern = rotations.packhamClarkeRotation?.pattern || ['Damian Clarke', 'Iain Packham'];
    const cycleWeeks = rotations.packhamClarkeRotation?.cycleWeeks || 8;
    return pattern[weeksSinceStart % cycleWeeks] || 'Iain Packham';
  };

  // Helper function to get current consultant (handles maternity cover)
  // dutyType: 'rota' for regular duties, 'oncall' for on-call duties
  const getCurrentConsultant = (originalName, currentDate, dutyType = 'rota') => {
    const consultant = consultants.find(c => c.name === originalName);
    if (consultant?.maternityCover) {
      const checkDate = new Date(currentDate);
      const maternityStart = new Date(consultant.maternityCover.startDate);

      // Use specific end date based on duty type, fall back to general endDate
      let maternityEnd;
      if (dutyType === 'oncall' && consultant.maternityCover.onCallEndDate) {
        maternityEnd = new Date(consultant.maternityCover.onCallEndDate);
      } else if (dutyType === 'rota' && consultant.maternityCover.rotaEndDate) {
        maternityEnd = new Date(consultant.maternityCover.rotaEndDate);
      } else {
        maternityEnd = new Date(consultant.maternityCover.endDate);
      }

      if (checkDate >= maternityStart && checkDate <= maternityEnd) {
        const coverConsultant = consultants.find(c => c.id === consultant.maternityCover.coveredBy);
        return coverConsultant ? coverConsultant.name : originalName;
      }
    }
    return originalName;
  };

  // Friday Theatre 4 assignment
  const getFridayTheatre4Assignment = (staffOnLeave, currentDate, weekNumber) => {
    const priorities = rotations.weeklySchedule?.friday?.theatre4Priority || [];

    // Lynn Hutchings on odd weeks (1, 3), Selina Graham on even weeks (2, 4)
    const isOddWeek = weekNumber % 2 === 1;

    for (const surgeonName of priorities) {
      const currentName = getCurrentConsultant(surgeonName, currentDate, 'rota');

      // Skip Lynn Hutchings on even weeks, Selina Graham on odd weeks
      if (currentName === 'Lynn Hutchings' && !isOddWeek) {
        continue;
      }
      if (currentName === 'Selina Graham' && isOddWeek) {
        continue;
      }

      const isAvailable = !staffOnLeave.some(l => l.name === currentName);

      if (isAvailable) {
        return { name: currentName, available: true };
      }
    }

    return { name: 'Needs surgeon', available: false };
  };

  // Get week number
  const getWeekNumber = (date) => {
    // Compare whole calendar days (UTC-based) so the cycle is stable regardless of
    // the browser timezone or the time-of-day on `date`. Reference: Mon 11 Aug 2025.
    const refUTC = Date.UTC(2025, 7, 11);
    const dayUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const diffWeeks = Math.floor((dayUTC - refUTC) / (7 * 24 * 60 * 60 * 1000));
    const cycleWeek = (((diffWeeks % 4) + 4) % 4) + 1; // 1..4, safe for negatives
    return cycleWeek;
  };

  // Get weekend on-call with overrides applied
  const getWeekendOnCall = (dateStr) => {
    // Check overrides first, then fall back to base rota
    const initials = weekendRotaOverrides[dateStr] || weekendRota[dateStr];
    return initials ? getConsultantByInitials(initials) : '';
  };

  // Get Friday on-call
  const getFridayOnCall = (weekStartDate) => {
    const friday = new Date(weekStartDate);
    friday.setDate(friday.getDate() + 4);
    const dateStr = `${friday.getFullYear()}-${String(friday.getMonth() + 1).padStart(2, '0')}-${String(friday.getDate()).padStart(2, '0')}`;
    return getWeekendOnCall(dateStr);
  };

  // Get on-call surgeon (without maternity cover - raw schedule)
  const getOnCallSurgeonRaw = (day, weekNumber, weekStartDate) => {
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const dayName = dayNames[day];

    if (day === 4) {
      const friday = new Date(weekStartDate);
      friday.setDate(friday.getDate() + 4);
      const dateStr = `${friday.getFullYear()}-${String(friday.getMonth() + 1).padStart(2, '0')}-${String(friday.getDate()).padStart(2, '0')}`;
      return getWeekendOnCall(dateStr);
    }

    const onCallSchedule = rotations.onCallSchedule?.[dayName];
    if (!onCallSchedule) return '';

    let surgeon = onCallSchedule[weekNumber];

    if (surgeon === 'PACKHAM_CLARKE') {
      return getPackhamClarkePattern(weekNumber, weekStartDate);
    } else if (surgeon === 'BAKER_BICK') {
      return getBakerBickPattern(weekStartDate);
    } else {
      return surgeon; // Return raw name without maternity cover transformation
    }
  };

  // Get on-call surgeon (with maternity cover applied)
  const getOnCallSurgeon = (day, weekNumber, weekStartDate) => {
    const rawSurgeon = getOnCallSurgeonRaw(day, weekNumber, weekStartDate);
    return getCurrentConsultant(rawSurgeon, weekStartDate, 'oncall');
  };

  // Get staff for day
  const getStaffForDay = (day, weekNumber, weekStartDate) => {
    const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const dayName = dayNames[day];
    const daySchedule = rotations.weeklySchedule?.[dayName];

    if (!daySchedule) return { flexible: [], assigned: [] };

    const flexible = daySchedule.flexible.map(name => {
      if (name === 'PACKHAM_CLARKE') {
        return getPackhamClarkePattern(weekNumber, weekStartDate);
      } else if (name === 'BAKER_BICK') {
        return getBakerBickPattern(weekStartDate);
      } else {
        return getCurrentConsultant(name, weekStartDate, 'rota');
      }
    });

    const assigned = daySchedule.assigned.map(assignment => {
      if (assignment.weeks && !assignment.weeks.includes(weekNumber)) {
        return null;
      }
      return {
        name: getCurrentConsultant(assignment.surgeon, weekStartDate, 'rota'),
        duty: assignment.duty
      };
    }).filter(Boolean);

    return { flexible, assigned };
  };

  // ---- Bulk Tuesday rota editor ----
  const dateKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Base (unswapped) Tuesday on-call surgeon for a given Tuesday Date.
  const tuesdayBaseOnCall = (tue) => {
    const weekStart = new Date(tue);
    weekStart.setDate(tue.getDate() - tue.getDay() + 1); // Monday of that week
    return getOnCallSurgeonRaw(1, getWeekNumber(weekStart), weekStart); // day 1 = Tuesday
  };

  const generateBulkRota = () => {
    if (!bulkStart || !bulkEnd) {
      alert('Please choose a start and end date.');
      return;
    }
    const [sy, sm, sd] = bulkStart.split('-').map(Number);
    const [ey, em, ed] = bulkEnd.split('-').map(Number);
    const end = new Date(ey, em - 1, ed);
    const d = new Date(sy, sm - 1, sd);
    while (d.getDay() !== 2) d.setDate(d.getDate() + 1); // advance to first Tuesday
    const map = {};
    while (d <= end) {
      const key = dateKeyOf(d);
      map[key] = swaps[key] ? swaps[key].newSurgeon : (offOnCallCover[key] || tuesdayBaseOnCall(d));
      d.setDate(d.getDate() + 7);
    }
    setBulkRota(map);
  };

  const applyBulkRota = async () => {
    if (!bulkRota) return;
    const adds = {};              // ordinary swaps (reshuffles among present staff)
    const removeKeys = [];        // swaps to remove
    const nextCover = { ...offOnCallCover };
    let coverChanged = false;
    let additionalCount = 0;
    Object.entries(bulkRota).forEach(([key, person]) => {
      const [y, m, dd] = key.split('-').map(Number);
      const base = tuesdayBaseOnCall(new Date(y, m - 1, dd));
      if (person && person !== base) {
        if (isOffOnCall(base, key)) {
          // Covering a slot whose base person is off the on-call rota (e.g. TPB pre-maternity)
          // → record as an ADDITIONAL shift so it flags for compensation.
          additionalCount++;
          if (nextCover[key] !== person) { nextCover[key] = person; coverChanged = true; }
          if (swaps[key]) removeKeys.push(key);
        } else {
          // A reshuffle among present staff → ordinary reassignment.
          adds[key] = { originalSurgeon: base, newSurgeon: person };
          if (nextCover[key]) { delete nextCover[key]; coverChanged = true; }
        }
      } else {
        // Put back to base → clear any change for this date.
        if (swaps[key]) removeKeys.push(key);
        if (nextCover[key]) { delete nextCover[key]; coverChanged = true; }
      }
    });
    try {
      if (Object.keys(adds).length > 0) {
        setSwaps(prev => ({ ...prev, ...adds }));
        await dataService.addSwapsBatch(adds);
      }
      for (const key of removeKeys) {
        await handleRemoveSwap(key);
      }
      if (coverChanged) {
        setOffOnCallCover(nextCover);
        await saveOverrides(offOnCall, nextCover);
      }
      alert(`Applied ${Object.keys(adds).length} reassignment(s) and ${additionalCount} additional cover(s).`);
    } catch (error) {
      console.error('Error applying bulk Tuesday rota:', error);
      const swapsData = await dataService.getSwaps();
      setSwaps(swapsData);
    }
  };

  // Calculate capacity
  const calculateCapacity = () => {
    const weeks = [];
    const today = new Date();

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - today.getDay() + 1);

    for (let w = 0; w < 140; w++) {
      const weekStart = new Date(startDate);
      weekStart.setDate(startDate.getDate() + (w * 7));

      const weekNumber = getWeekNumber(weekStart);
      const days = [];

      for (let day = 0; day < 5; day++) {
        const dayDate = new Date(weekStart);
        dayDate.setDate(weekStart.getDate() + day);
        dayDate.setHours(0, 0, 0, 0); // Normalize to midnight

        const staffOnLeave = leave.filter(l => {
          const start = new Date(l.startDate);
          start.setHours(0, 0, 0, 0); // Normalize to midnight
          const end = new Date(l.endDate);
          end.setHours(0, 0, 0, 0); // Normalize to midnight
          return dayDate >= start && dayDate <= end;
        });

        const staffForDay = getStaffForDay(day, weekNumber, weekStart);

        // Get raw on-call surgeon (before maternity cover)
        const rawOnCallSurgeon = getOnCallSurgeonRaw(day, weekNumber, weekStart);

        // Use actual date as key for persistence across weeks
        // Construct YYYY-MM-DD from local date to avoid timezone issues
        const dateKey = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
        const swap = swaps[dateKey];

        // Apply swap to raw on-call surgeon if it matches, then apply maternity cover
        let onCallSurgeon;
        if (swap && rawOnCallSurgeon === swap.originalSurgeon) {
          // Use swapped surgeon (and apply maternity cover to them if needed)
          onCallSurgeon = getCurrentConsultant(swap.newSurgeon, weekStart, 'oncall');
        } else {
          // No swap, use original with maternity cover applied
          onCallSurgeon = getCurrentConsultant(rawOnCallSurgeon, weekStart, 'oncall');
        }

        let updatedStaffForDay = { ...staffForDay };

        // Add availability flag to all assigned staff
        updatedStaffForDay.assigned = updatedStaffForDay.assigned.map(a => ({
          ...a,
          available: !staffOnLeave.some(l => l.name === a.name)
        }));

        if (day === 4) {
          const fridayAssignment = getFridayTheatre4Assignment(staffOnLeave, dayDate, weekNumber);
          updatedStaffForDay.assigned.push({
            name: fridayAssignment.name,
            duty: 'Theatre 4',
            available: fridayAssignment.available
          });
        }

        // Each surgeon should appear in only one place per day: drop anyone who is
        // assigned to a specific duty (or is the on-call surgeon) from the flexible pool.
        const placedNames = new Set(updatedStaffForDay.assigned.map(a => a.name));
        if (onCallSurgeon) placedNames.add(onCallSurgeon);
        updatedStaffForDay.flexible = updatedStaffForDay.flexible.filter(name => !placedNames.has(name));

        // Get additional people for this specific date
        const additionalPeopleForDay = additionalPeople[dateKey] || [];

        // Calculate expected staffing for this day using unique names
        // Collect all staff names (flexible + assigned + on-call) and deduplicate
        const allExpectedNames = new Set();

        // Add flexible staff
        updatedStaffForDay.flexible.forEach(name => allExpectedNames.add(name));

        // Add assigned staff (including Pelvic team as 1 person).
        // 'Needs surgeon' is an unfilled-slot placeholder, not a real surgeon — don't count it.
        updatedStaffForDay.assigned.forEach(a => {
          if (a.name && typeof a.name === 'string') {
            if (a.name === 'Pelvic team') {
              allExpectedNames.add('Pelvic team'); // Count as 1 person
            } else if (!a.name.includes('team') && a.name !== 'Needs surgeon') {
              allExpectedNames.add(a.name);
            }
          }
        });

        // Add on-call surgeon
        if (onCallSurgeon) {
          allExpectedNames.add(onCallSurgeon);
        }

        // Add additional people for this day
        additionalPeopleForDay.forEach(name => allExpectedNames.add(name));

        // Long-term absence (maternity leave) is removed from the expected roster
        // entirely, so the expected total is downgraded. The required minimum (below)
        // is unchanged. Regular leave still counts toward expected, just not available.
        staffOnLeave
          .filter(l => l.reason && l.reason.toLowerCase().includes('maternity'))
          .forEach(l => allExpectedNames.delete(l.name));
        const expectedStaff = allExpectedNames.size;

        // Calculate available staffing (unique names not on leave)
        const allAvailableNames = new Set();

        // Filter available flexible staff first
        const availableFlexibleStaff = updatedStaffForDay.flexible.filter(name =>
          !staffOnLeave.some(l => l.name === name)
        );

        // Add available flexible staff
        availableFlexibleStaff.forEach(name => allAvailableNames.add(name));

        // Add available assigned staff (including Pelvic team as 1 person).
        // Use the assignment's own availability flag, which is false for staff on
        // leave and for the 'Needs surgeon' unfilled-slot placeholder.
        updatedStaffForDay.assigned.forEach(a => {
          if (a.name && typeof a.name === 'string') {
            if (a.name === 'Pelvic team') {
              allAvailableNames.add('Pelvic team'); // Always available as a unit
            } else if (!a.name.includes('team') && a.name !== 'Needs surgeon' && a.available) {
              allAvailableNames.add(a.name);
            }
          }
        });

        // Check if on-call surgeon is on leave
        const onCallSurgeonOnLeave = onCallSurgeon && staffOnLeave.some(l => l.name === onCallSurgeon);

        // Add on-call if available
        if (onCallSurgeon && !onCallSurgeonOnLeave) {
          allAvailableNames.add(onCallSurgeon);
        }

        // Add available additional people (not on leave)
        const availableAdditionalPeople = additionalPeopleForDay.filter(name =>
          !staffOnLeave.some(l => l.name === name)
        );
        availableAdditionalPeople.forEach(name => allAvailableNames.add(name));

        const availableStaff = allAvailableNames.size;

        // Check if Friday Theatre 4 is available
        let fridayTheatre4Available = true;
        if (day === 4) {
          const fridayAssignment = updatedStaffForDay.assigned.find(a => a.duty === 'Theatre 4');
          fridayTheatre4Available = fridayAssignment?.available || false;
        }

        // Check for critical assignments on leave
        const criticalAssignmentsOnLeave = updatedStaffForDay.assigned.filter(a =>
          a.name && !a.name.includes('team') && staffOnLeave.some(l => l.name === a.name)
        );

        // Activity requirements per day: Mon/Tue/Thu/Fri = 4, Wed = 5
        // Add count of additional activities
        const additionalActivitiesForDay = additionalActivities[dateKey] || [];
        const additionalActivityCount = Array.isArray(additionalActivitiesForDay) ? additionalActivitiesForDay.length : 0;
        const baseActivities = (day === 2) ? 5 : 4;
        const requiredActivities = baseActivities + additionalActivityCount;

        // Determine status with different categories
        let status = 'adequate';

        // Check if on-call is on leave (priority indicator)
        if (onCallSurgeonOnLeave) {
          status = 'oncall_leave';
        }
        // Check for critical assignments on leave or Friday theatre issues
        else if (criticalAssignmentsOnLeave.length > 0 || (day === 4 && !fridayTheatre4Available)) {
          status = 'critical';
        }
        // Check for understaffing
        else if (availableStaff <= requiredActivities - 1) {
          // 3 or below for Mon/Tue/Thu/Fri, 4 or below for Wed
          status = 'critical';
        } else if (availableStaff === requiredActivities) {
          // Exactly 4 for Mon/Tue/Thu/Fri, exactly 5 for Wed
          status = 'shortage';
        } else {
          // 5+ for Mon/Tue/Thu/Fri, 6+ for Wed
          status = 'surplus';
        }

        // Calculate percentage for display compatibility
        const capacityPercentage = Math.round((availableStaff / expectedStaff) * 100);

        days.push({
          date: dayDate,
          weekNumber,
          dayOfWeek: day,
          available: availableStaff,
          required: expectedStaff,
          capacityPercentage,
          status,
          onLeave: staffOnLeave,
          assigned: updatedStaffForDay.assigned,
          flexible: updatedStaffForDay.flexible,
          availableFlexible: availableFlexibleStaff,
          onCall: onCallSurgeon,
          onCallOff: onCallSurgeon
            ? offOnCall.some(o => o.name === onCallSurgeon && dateKey >= o.fromDate && (!o.toDate || dateKey <= o.toDate))
            : false,
          onCallCover: offOnCallCover[dateKey] || null,
          swap,
          criticalAssignmentsOnLeave,
          onCallSurgeonOnLeave,
          additionalActivities: additionalActivitiesForDay,
          requiredActivities,
          additionalPeople: additionalPeopleForDay,
          availableAdditionalPeople
        });
      }

      weeks.push({
        weekNumber: w,
        startDate: weekStart,
        days
      });
    }

    return weeks;
  };

  const addLeave = async () => {
    if (selectedSurgeon && leaveStartDate && leaveEndDate && selectedLeaveReason) {
      if (editingLeave) {
        // Delete old entry then add updated one
        await handleRemoveLeave(editingLeave.id);
        setEditingLeave(null);
      }
      const leaveEntry = {
        name: selectedSurgeon,
        startDate: leaveStartDate,
        endDate: leaveEndDate,
        reason: selectedLeaveReason
      };
      handleAddLeave(leaveEntry);
      setSelectedSurgeon('');
      setSelectedLeaveReason('');
      setLeaveStartDate('');
      setLeaveEndDate('');
    }
  };

  const startEditLeave = (leaveEntry) => {
    setEditingLeave(leaveEntry);
    setSelectedSurgeon(leaveEntry.name);
    setLeaveStartDate(leaveEntry.startDate);
    setLeaveEndDate(leaveEntry.endDate);
    setSelectedLeaveReason(leaveEntry.reason);
    // Scroll to leave form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const addSwap = async (weekIndex, dayIndex) => {
    if (selectedSwapSurgeon1 && selectedSwapSurgeon2) {
      // Calculate actual date from weekIndex and dayIndex
      const today = new Date();
      const currentWeekMonday = new Date(today);
      currentWeekMonday.setDate(today.getDate() - today.getDay() + 1);
      currentWeekMonday.setHours(0, 0, 0, 0);

      const weekStart = new Date(currentWeekMonday);
      weekStart.setDate(currentWeekMonday.getDate() + (parseInt(weekIndex) * 7));
      const swapDate = new Date(weekStart);
      swapDate.setDate(weekStart.getDate() + parseInt(dayIndex));

      // Use actual date as key for persistence across weeks
      const key = `${swapDate.getFullYear()}-${String(swapDate.getMonth() + 1).padStart(2, '0')}-${String(swapDate.getDate()).padStart(2, '0')}`;
      const swap = {
        originalSurgeon: selectedSwapSurgeon1,
        newSurgeon: selectedSwapSurgeon2
      };

      // If editing an existing swap and applying to a different day, delete the old one first
      if (editingSwap && editingSwap !== key) {
        await handleRemoveSwap(editingSwap);
      }

      await handleAddSwap(key, swap);
      setSelectedSwapSurgeon1('');
      setSelectedSwapSurgeon2('');
      setEditingSwap(null);
    }
  };

  const startEditSwap = (key, swap) => {
    setEditingSwap(key);
    setSelectedSwapSurgeon1(swap.originalSurgeon);
    setSelectedSwapSurgeon2(swap.newSurgeon);
    setOpenSwapDropdown(null);
  };

  const deleteSwap = async (key) => {
    await handleRemoveSwap(key);
    setOpenSwapDropdown(null);
  };

  const cancelEditSwap = () => {
    setEditingSwap(null);
    setSelectedSwapSurgeon1('');
    setSelectedSwapSurgeon2('');
  };

  const weeks = calculateCapacity();

  // Shared button styles — one accent (blue) for primary actions, a neutral
  // outline for secondary/alternate actions, so the controls read as one system.
  const btnPrimary = "px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed";
  const btnSecondary = "px-3 py-1.5 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors font-medium";

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 flex items-center justify-center">
        <div className="text-lg">Loading data...</div>
      </div>
    );
  }

  // ---- Calendar data (shared across the Overview and Longer-term Calendar tabs) ----
  const todayForCal = new Date();
  const currentWeekIndex = weeks.findIndex(week => {
    const weekEnd = new Date(week.startDate);
    weekEnd.setDate(weekEnd.getDate() + 4); // Friday
    return week.startDate <= todayForCal && todayForCal <= weekEnd;
  });
  const startIndex = currentWeekIndex >= 0 ? currentWeekIndex : 0;
  const overview12Weeks = weeks.slice(startIndex, startIndex + 12);
  const remainingWeeks = weeks.slice(startIndex + 12);

  // Render an on-call name, flagging it when that person is off the on-call rota:
  // shows the assigned cover as an "additional" shift, or "cover needed" if unassigned.
  const renderOnCallName = (name, dateStr) => {
    if (name && name !== '—' && isOffOnCall(name, dateStr)) {
      const cover = offOnCallCover[dateStr];
      return cover
        ? (<span>{cover} <span className="text-[10px] font-semibold text-amber-800 bg-amber-100 rounded px-1 align-middle">additional</span></span>)
        : (<span><span className="line-through text-slate-400">{name}</span> <span className="text-[10px] font-semibold text-red-700 bg-red-100 rounded px-1 align-middle">cover needed</span></span>);
    }
    return name;
  };

  const renderWeekRow = (week) => (
    <tr
      key={week.weekNumber}
      className={`cursor-pointer [&>td]:border-b-2 [&>td]:border-white hover:[&>td]:brightness-95 ${
        selectedWeek?.weekNumber === week.weekNumber ? 'bg-blue-100' : week.weekNumber % 2 === 0 ? 'bg-gray-50' : 'bg-white'
      }`}
      onClick={() => {
        setSelectedWeek(week);
        setIsModalOpen(true);
      }}
    >
      <td className="px-4 py-2 text-sm">
        <div className="font-medium">
          {(() => {
            const startDay = week.startDate.getDate();
            const endDate = new Date(week.startDate);
            endDate.setDate(endDate.getDate() + 4); // Friday
            const endDay = endDate.getDate();
            const month = endDate.toLocaleDateString('en-GB', { month: 'short' });
            return `${startDay}-${endDay} ${month}`;
          })()}
        </div>
        <div className="text-xs text-gray-500">
          Cycle: Week {week.days[0].weekNumber}
        </div>
      </td>
      {week.days.map((day, dayIndex) => {
        const style = getStatusStyle(day.status);

        return (
          <td
            key={dayIndex}
            className={`px-4 py-3 text-center border-r border-white/70 ${style.table}`}
          >
            <div className="text-lg font-semibold">
              {day.available}
            </div>
            <div className="text-xs opacity-70">
              {day.capacityPercentage}%
            </div>
            {day.swap && (
              <div className="text-xs text-blue-600 mt-1">Swap</div>
            )}
            {day.additionalActivities && day.additionalActivities.length > 0 && (
              <div className="text-xs text-blue-700 font-semibold mt-1 bg-blue-200 rounded px-1">
                +{day.additionalActivities.length} {day.additionalActivities.length === 1 ? 'Activity' : 'Activities'}
              </div>
            )}
            {day.additionalPeople && day.additionalPeople.length > 0 && (
              <div className="text-xs text-indigo-700 font-semibold mt-1 bg-indigo-200 rounded px-1">
                +{day.additionalPeople.length} People
              </div>
            )}
          </td>
        );
      })}
      <td className="px-4 py-2 text-xs" onClick={e => e.stopPropagation()}>
        {(() => {
          const sat = new Date(week.startDate);
          sat.setDate(sat.getDate() + 5);
          const sun = new Date(week.startDate);
          sun.setDate(sun.getDate() + 6);
          const satKey = `${sat.getFullYear()}-${String(sat.getMonth() + 1).padStart(2, '0')}-${String(sat.getDate()).padStart(2, '0')}`;
          const sunKey = `${sun.getFullYear()}-${String(sun.getMonth() + 1).padStart(2, '0')}-${String(sun.getDate()).padStart(2, '0')}`;
          return (
            <div className="space-y-1">
              <div><span className="font-semibold text-gray-500">Sat:</span> {renderOnCallName(getWeekendOnCall(satKey) || '—', satKey)}</div>
              <div><span className="font-semibold text-gray-500">Sun:</span> {renderOnCallName(getWeekendOnCall(sunKey) || '—', sunKey)}</div>
            </div>
          );
        })()}
      </td>
      <td className="px-4 py-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSelectedWeek(week);
            setIsModalOpen(true);
          }}
          className="text-sm text-blue-500 hover:text-blue-700"
        >
          Details
        </button>
      </td>
    </tr>
  );

  const renderYearMarker = (year) => (
    <tr key={`year-${year}`} className="bg-gradient-to-r from-slate-600 to-slate-700">
      <td colSpan="8" className="px-4 py-3 text-center">
        <div className="text-xl font-bold text-white tracking-wider">
          {year}
        </div>
      </td>
    </tr>
  );

  const renderWeeksWithYearMarkers = (weeksToRender) => {
    const rows = [];
    let previousYear = null;
    weeksToRender.forEach((week) => {
      const currentYear = week.startDate.getFullYear();
      if (previousYear !== null && currentYear !== previousYear) {
        rows.push(renderYearMarker(currentYear));
      }
      rows.push(renderWeekRow(week));
      previousYear = currentYear;
    });
    return rows;
  };

  const statusLegend = (
    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
      <h3 className="text-lg font-semibold text-slate-800 mb-4">Status Legend</h3>
      <div className="space-y-2 text-sm">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 border rounded ${getStatusStyle('critical').dot}`}></div>
            <span><strong>Shortage</strong> (≤3 Mon/Tue/Thu/Fri, ≤4 Wed)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 border rounded ${getStatusStyle('shortage').dot}`}></div>
            <span><strong>Adequate</strong> (4 Mon/Tue/Thu/Fri, 5 Wed)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 border rounded ${getStatusStyle('surplus').dot}`}></div>
            <span><strong>Surplus</strong> (≥5 Mon/Tue/Thu/Fri, ≥6 Wed)</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center space-x-2">
            <span className="text-blue-600 font-semibold">Swap</span>
            <span>On-call swap indicator</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 border rounded ${getStatusStyle('oncall_leave').dot}`}></div>
            <span><strong>On-Call on Leave</strong></span>
          </div>
        </div>
      </div>
    </div>
  );

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'admin', label: 'Admin' },
    { id: 'longterm', label: 'Longer-term Calendar' },
    { id: 'leave', label: 'Leave Diary' },
  ];

  // Rest of the component JSX remains the same as the original
  return (
    <div className="p-6 bg-slate-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 border-b border-slate-200 pb-5">
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Trauma Surgery Daily Capacity Planner</h1>
          <p className="text-sm text-slate-500 mt-1">Daily theatre and on-call capacity overview</p>
        </header>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-1 mb-6 border-b border-slate-200">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border-b-2 transition-colors ${
                activeTab === t.id
                  ? 'border-blue-600 text-blue-700 bg-white'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== ADMIN TAB: leave + swap controls ===== */}
        {activeTab === 'admin' && (
        <>
        {/* Controls Section */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          {/* Leave Management */}
          <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">
              Leave Management
              {editingLeave && <span className="ml-2 text-sm text-orange-600 font-normal">(Editing existing entry)</span>}
            </h3>
            <p className="text-xs text-gray-500 mb-2">
              Click a week in the calendar to auto-fill dates, or enter manually
            </p>
            <div className="space-y-2 text-sm">
              <select
                value={selectedSurgeon}
                onChange={(e) => setSelectedSurgeon(e.target.value)}
                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select surgeon...</option>
                {getConsultantNames().map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  placeholder="Start date"
                  className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={leaveStartDate}
                  onChange={(e) => setLeaveStartDate(e.target.value)}
                />
                <input
                  type="date"
                  placeholder="End date"
                  className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={leaveEndDate}
                  onChange={(e) => setLeaveEndDate(e.target.value)}
                />
              </div>
              <select
                value={selectedLeaveReason}
                onChange={(e) => setSelectedLeaveReason(e.target.value)}
                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Reason for leave...</option>
                {leaveReasons.map(reason => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button
                  onClick={addLeave}
                  disabled={!selectedSurgeon || !leaveStartDate || !leaveEndDate || !selectedLeaveReason}
                  className={`flex-1 ${btnPrimary}`}
                >
                  {editingLeave ? 'Update Leave' : 'Add Leave'}
                </button>
                {editingLeave && (
                  <button
                    onClick={() => {
                      setEditingLeave(null);
                      setSelectedSurgeon('');
                      setSelectedLeaveReason('');
                      setLeaveStartDate('');
                      setLeaveEndDate('');
                    }}
                    className={btnSecondary}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Job Share Swaps */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Job Share Swaps</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <h4 className="font-medium mb-2">Packham/Clarke Swap (Monday)</h4>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-1">
                  <input
                    type="date"
                    className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    placeholder="Monday date"
                    value={packhamClarkeDate}
                    onChange={(e) => setPackhamClarkeDate(e.target.value)}
                  />
                  <select
                    className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    value={packhamClarkePerson}
                    onChange={(e) => setPackhamClarkePerson(e.target.value)}
                  >
                    <option value="">Select person...</option>
                    <option value="Iain Packham">Iain Packham</option>
                    <option value="Damian Clarke">Damian Clarke</option>
                  </select>
                  <button
                    onClick={addPackhamClarkeSwap}
                    className={`${btnPrimary} text-xs`}
                  >
                    Set Monday
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  Select any date in the week to set who works Monday
                </div>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <h4 className="font-medium mb-2">Baker/Bick Swap (Wednesday)</h4>
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-3 gap-1">
                  <input
                    type="date"
                    className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    placeholder="Wednesday date"
                    value={bakerBickDate}
                    onChange={(e) => setBakerBickDate(e.target.value)}
                  />
                  <select
                    className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs"
                    value={bakerBickPerson}
                    onChange={(e) => setBakerBickPerson(e.target.value)}
                  >
                    <option value="">Select person...</option>
                    <option value="Rich Baker">Rich Baker</option>
                    <option value="Simon Bick">Simon Bick</option>
                  </select>
                  <button
                    onClick={addBakerBickSwap}
                    className={`${btnPrimary} text-xs`}
                  >
                    Set Wednesday
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  Select any date in the week to set who works Wednesday
                </div>
              </div>
            </div>
          </div>

          {(Object.keys(packhamClarkeOverrides).length > 0 || Object.keys(bakerBickOverrides).length > 0) && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(packhamClarkeOverrides).length > 0 && (
                <div className="p-3 bg-blue-50 rounded">
                  <h5 className="font-medium mb-2">Packham/Clarke Swaps:</h5>
                  <div className="space-y-1">
                    {Object.entries(packhamClarkeOverrides).map(([weekKey, person]) => {
                      const date = new Date(weekKey);
                      return (
                        <div key={weekKey} className="flex justify-between items-center text-sm">
                          <span>
                            {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} (Mon): <strong>{person}</strong>
                          </span>
                          <button
                            onClick={() => removePackhamClarkeSwap(weekKey)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {Object.keys(bakerBickOverrides).length > 0 && (
                <div className="p-3 bg-green-50 rounded">
                  <h5 className="font-medium mb-2">Baker/Bick Swaps:</h5>
                  <div className="space-y-1">
                    {Object.entries(bakerBickOverrides).map(([weekKey, person]) => {
                      const date = new Date(weekKey);
                      const wednesday = new Date(date);
                      wednesday.setDate(date.getDate() + 2);
                      return (
                        <div key={weekKey} className="flex justify-between items-center text-sm">
                          <span>
                            {wednesday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} (Wed): <strong>{person}</strong>
                          </span>
                          <button
                            onClick={() => removeBakerBickSwap(weekKey)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* On-Call Swaps */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">On-Call Swaps</h3>

          {/* Swap between two dates */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Swap between two dates</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-sm mb-3">
            <input
              type="date"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={swapDate1}
              onChange={(e) => setSwapDate1(e.target.value)}
            />
            <select
              value={selectedSwapSurgeon1}
              onChange={(e) => setSelectedSwapSurgeon1(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select first surgeon...</option>
              {getConsultantNames().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <input
              type="date"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={swapDate2}
              onChange={(e) => setSwapDate2(e.target.value)}
            />
            <select
              value={selectedSwapSurgeon2}
              onChange={(e) => setSelectedSwapSurgeon2(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select second surgeon...</option>
              {getConsultantNames().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <button
              onClick={addDoubleSwap}
              className={btnPrimary}
            >
              Swap Between
            </button>
          </div>

          {/* Single change */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Single change</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mb-3">
            <input
              type="date"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={onCallSingleDate}
              onChange={(e) => setOnCallSingleDate(e.target.value)}
            />
            <select
              value={onCallSinglePerson}
              onChange={(e) => setOnCallSinglePerson(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select surgeon...</option>
              {getConsultantNames().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <button
              onClick={addSingleSwap}
              className={btnSecondary}
            >
              Single Change
            </button>
          </div>
          <div className="text-xs text-gray-500">
            "Swap Between" exchanges two surgeons' on-call duties across two dates; "Single Change" reassigns a single date to a chosen surgeon.
          </div>
        </div>

        {/* Weekend On-Call Changes */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Weekend On-Call Swaps</h3>

          {/* Swap between two dates */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Swap between two dates</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-sm mb-3">
            <input
              type="date"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={weekendSwapDate1}
              onChange={(e) => setWeekendSwapDate1(e.target.value)}
            />
            <select
              value={weekendSwapSurgeon1}
              onChange={(e) => setWeekendSwapSurgeon1(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select first consultant...</option>
              {consultants.map(c => (
                <option key={c.id} value={c.initials}>{c.name}</option>
              ))}
            </select>
            <input
              type="date"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={weekendSwapDate2}
              onChange={(e) => setWeekendSwapDate2(e.target.value)}
            />
            <select
              value={weekendSwapSurgeon2}
              onChange={(e) => setWeekendSwapSurgeon2(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select second consultant...</option>
              {consultants.map(c => (
                <option key={c.id} value={c.initials}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={addWeekendSwap}
              className={btnPrimary}
            >
              Swap Between
            </button>
          </div>

          {/* Single change */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Single change</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mb-3">
            <input
              type="date"
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={weekendChangeDate}
              onChange={(e) => setWeekendChangeDate(e.target.value)}
              placeholder="Select date"
            />
            <select
              value={weekendChangeConsultant}
              onChange={(e) => setWeekendChangeConsultant(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select consultant...</option>
              {consultants.map(c => (
                <option key={c.id} value={c.initials}>{c.name}</option>
              ))}
            </select>
            <button
              onClick={async () => {
                if (weekendChangeDate && weekendChangeConsultant) {
                  await handleAddWeekendChange(weekendChangeDate, weekendChangeConsultant);
                  setWeekendChangeDate('');
                  setWeekendChangeConsultant('');
                }
              }}
              className={btnSecondary}
            >
              Single Change
            </button>
          </div>
          <div className="text-xs text-gray-500 mb-2">
            "Swap Between" exchanges the on-call consultants between two weekend dates; "Single Change" sets who is on-call for a specific weekend date (Fri/Sat/Sun).
          </div>
          {(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const upcomingChanges = Object.entries(weekendRotaOverrides)
              .filter(([date]) => {
                const [year, month, day] = date.split('-').map(Number);
                return new Date(year, month - 1, day) >= today;
              })
              .sort((a, b) => new Date(a[0]) - new Date(b[0]));

            if (upcomingChanges.length === 0) return null;

            return (
              <div className="mt-3">
                <h4 className="font-semibold text-sm mb-2">Current Changes:</h4>
                <div className="space-y-1">
                  {upcomingChanges.map(([date, initials]) => {
                    const consultant = consultants.find(c => c.initials === initials);
                    const baseInitials = weekendRota[date];
                    const baseConsultant = baseInitials ? consultants.find(c => c.initials === baseInitials) : null;
                    return (
                      <div key={date} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                        <span>
                          <strong>{new Date(date).toLocaleDateString('en-GB')}</strong>
                          {baseConsultant && (
                            <span className="text-gray-500"> (was {baseConsultant.name})</span>
                          )}
                          {' → '}{consultant?.name || initials}
                        </span>
                        <button
                          onClick={() => handleRemoveWeekendChange(date)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* On-Call Rota Absence (off on-call, still working normal days) */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">On-Call Rota Absence</h3>
          <p className="text-xs text-gray-500 mb-4">
            Take a consultant off the on-call rota from a date (e.g. before maternity leave) while they still work normal days.
            Their Tuesday and weekend on-calls then show as needing cover; assign a cover name to record it as an additional shift.
          </p>

          {/* Take someone off the on-call rota (from / to; leave "to" blank for open-ended) */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm mb-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide self-center">Off the on-call rota</span>
            <span className="hidden md:block text-xs text-slate-400 self-center">From</span>
            <span className="hidden md:block text-xs text-slate-400 self-center">To (blank = ongoing)</span>
            <span></span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm mb-3">
            <select
              value={offOnCallPerson}
              onChange={(e) => setOffOnCallPerson(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select consultant...</option>
              {getConsultantNames().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <input
              type="date"
              value={offOnCallFrom}
              onChange={(e) => setOffOnCallFrom(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="date"
              value={offOnCallTo}
              onChange={(e) => setOffOnCallTo(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button onClick={() => addOffOnCall(offOnCallPerson, offOnCallFrom, offOnCallTo)} className={btnPrimary}>
              Take Off On-Call
            </button>
          </div>

          {offOnCall.length > 0 && (
            <div className="space-y-1 mb-4">
              {offOnCall.map(o => (
                <div key={o.name} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                  <span><strong>{o.name}</strong> — off on-call from {o.fromDate.split('-').reverse().join('/')}{o.toDate ? ` to ${o.toDate.split('-').reverse().join('/')}` : ' (ongoing)'}</span>
                  <button onClick={() => removeOffOnCall(o.name)} className="text-red-600 hover:text-red-800">✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Assign cover for a specific on-call date (an additional shift) */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Assign cover for a date (additional shift)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm mb-3">
            <input
              type="date"
              value={coverDate}
              onChange={(e) => setCoverDate(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={coverPerson}
              onChange={(e) => setCoverPerson(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select cover...</option>
              {getConsultantNames().map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            <button onClick={() => setOnCallCover(coverDate, coverPerson)} className={btnSecondary}>
              Assign Cover
            </button>
          </div>

          {Object.keys(offOnCallCover).length > 0 && (
            <>
              <div className="space-y-1">
                {Object.entries(offOnCallCover)
                  .sort((a, b) => new Date(a[0]) - new Date(b[0]))
                  .map(([d, name]) => (
                    <div key={d} className="flex justify-between items-center text-sm p-2 bg-amber-50 border border-amber-200 rounded">
                      <span>{d.split('-').reverse().join('/')}: <strong>{name}</strong> <span className="text-[10px] font-semibold text-amber-800 bg-amber-100 rounded px-1 align-middle">additional</span></span>
                      <button onClick={() => setOnCallCover(d, '')} className="text-red-600 hover:text-red-800">✕</button>
                    </div>
                  ))}
              </div>
              {(() => {
                const tally = {};
                Object.values(offOnCallCover).forEach(n => { tally[n] = (tally[n] || 0) + 1; });
                return (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Additional shifts per person (for compensation)</div>
                    <div className="text-sm">
                      {Object.entries(tally).sort((a, b) => b[1] - a[1]).map(([n, c]) => (
                        <div key={n} className="flex justify-between p-1.5 border-b border-slate-100">
                          <span>{n}</span><span className="font-semibold">{c}</span>
                        </div>
                      ))}
                      <div className="flex justify-between p-1.5 font-semibold text-slate-700">
                        <span>Total additional shifts</span><span>{Object.keys(offOnCallCover).length}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {/* Bulk Tuesday Rota editor */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Bulk Tuesday Rota</h3>
          <p className="text-xs text-gray-500 mb-4">
            Reassign the Tuesday on-call in bulk. Choose a date range, then set the actual person for each Tuesday
            (pre-filled with the base rota / any existing change). "Apply all" writes every change at once; rows put back to the base person are reverted.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm mb-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide self-center">Date range</span>
            <span className="hidden md:block text-xs text-slate-400 self-center">From</span>
            <span className="hidden md:block text-xs text-slate-400 self-center">To</span>
            <span></span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm mb-3">
            <span className="hidden md:block"></span>
            <input
              type="date"
              value={bulkStart}
              onChange={(e) => setBulkStart(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <input
              type="date"
              value={bulkEnd}
              onChange={(e) => setBulkEnd(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-md text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button onClick={generateBulkRota} className={btnPrimary}>Generate</button>
          </div>

          {bulkRota && (() => {
            const keys = Object.keys(bulkRota).sort();
            const changes = keys.filter(k => {
              const [y, m, d] = k.split('-').map(Number);
              return bulkRota[k] !== tuesdayBaseOnCall(new Date(y, m - 1, d));
            }).length;
            return (
              <>
                <div className="text-xs text-slate-600 mb-2">
                  {keys.length} Tuesdays · <span className="font-semibold text-amber-700">{changes} change(s)</span>
                </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-600 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Tuesday</th>
                          <th className="text-left px-3 py-2 font-semibold">Base</th>
                          <th className="text-left px-3 py-2 font-semibold">On call</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map(k => {
                          const [y, m, d] = k.split('-').map(Number);
                          const dt = new Date(y, m - 1, d);
                          const base = tuesdayBaseOnCall(dt);
                          const changed = bulkRota[k] !== base;
                          return (
                            <tr key={k} className={changed ? 'bg-amber-50' : 'odd:bg-white even:bg-slate-50'}>
                              <td className="px-3 py-1.5 whitespace-nowrap">{dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                              <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{base}</td>
                              <td className="px-3 py-1.5">
                                <select
                                  value={bulkRota[k]}
                                  onChange={(e) => setBulkRota(prev => ({ ...prev, [k]: e.target.value }))}
                                  className={`w-full px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${changed ? 'border-amber-400 bg-white font-medium text-slate-800' : 'border-slate-300 text-slate-600'}`}
                                >
                                  {getConsultantNames().map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <button onClick={applyBulkRota} className={btnPrimary}>Apply all changes</button>
              </>
            );
          })()}
        </div>

        </>
        )}

        {/* ===== OVERVIEW TAB ===== */}
        {activeTab === 'overview' && (
          <>
              {/* 12-Week Overview */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">12-Week Overview</h2>
                <div className="text-sm text-gray-600 mb-2">
                  Next 12 weeks from {overview12Weeks.length > 0 ? overview12Weeks[0].startDate.toLocaleDateString('en-GB') : 'current week'}
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full table-fixed">
                      <thead className="bg-slate-700 text-white">
                        <tr>
                          <th className="w-[18%] px-4 py-3 text-left">Week</th>
                          <th className="w-[10%] px-4 py-3 text-center">Mon</th>
                          <th className="w-[10%] px-4 py-3 text-center">Tue</th>
                          <th className="w-[10%] px-4 py-3 text-center">Wed</th>
                          <th className="w-[10%] px-4 py-3 text-center">Thu</th>
                          <th className="w-[10%] px-4 py-3 text-center">Fri</th>
                          <th className="w-[20%] px-4 py-3 text-left">Weekend On-Call</th>
                          <th className="w-[12%] px-4 py-3 text-left">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderWeeksWithYearMarkers(overview12Weeks)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              {statusLegend}
          </>
        )}

        {/* ===== ADMIN TAB: current swaps ===== */}
        {activeTab === 'admin' && (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const upcomingSwaps = Object.entries(swaps)
                  .filter(([key]) => {
                    const [year, month, day] = key.split('-').map(Number);
                    return new Date(year, month - 1, day) >= today;
                  })
                  .sort((a, b) => new Date(a[0]) - new Date(b[0]));

                if (upcomingSwaps.length === 0) return null;

                return (
                  <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">Current Swaps</h3>
                    <div className="space-y-1">
                      {upcomingSwaps.map(([key, swap]) => {
                        // Key is an actual date (YYYY-MM-DD). Parse components to avoid timezone issues.
                        const [year, month, day] = key.split('-').map(Number);
                        const swapDate = new Date(year, month - 1, day);
                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                        const dayName = dayNames[swapDate.getDay()];
                        const formattedDate = `${day} ${swapDate.toLocaleDateString('en-GB', { month: 'short' })} ${year}`;

                        return (
                          <div key={key} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                            <span>
                              {formattedDate} ({dayName}): <strong>{swap.originalSurgeon}</strong> → <strong>{swap.newSurgeon}</strong>
                            </span>
                            <button
                              onClick={() => deleteSwap(key)}
                              className="text-red-600 hover:text-red-800"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ===== LEAVE DIARY TAB ===== */}
              {activeTab === 'leave' && leave.length > 0 && (() => {
                const currentYear = new Date().getFullYear();

                // Show this year and future years (past years drop off), for people
                // still on the consultant list. Entries are grouped per person, then
                // subdivided by year (e.g. 2026, 2027).
                const currentNames = new Set(consultants.map(c => c.name));
                const shownLeave = leave.filter(l => {
                  if (!currentNames.has(l.name)) return false;
                  const startYear = new Date(l.startDate).getFullYear();
                  const endYear = new Date(l.endDate).getFullYear();
                  return startYear >= currentYear || endYear >= currentYear;
                });

                if (shownLeave.length === 0) return null;

                // consultant -> { year -> [entries] }
                const byConsultant = {};
                shownLeave.forEach(l => {
                  const startYear = new Date(l.startDate).getFullYear();
                  const groupYear = Math.max(startYear, currentYear); // clamp so nothing lands before this year
                  if (!byConsultant[l.name]) byConsultant[l.name] = {};
                  if (!byConsultant[l.name][groupYear]) byConsultant[l.name][groupYear] = [];
                  byConsultant[l.name][groupYear].push(l);
                });
                const peopleCount = Object.keys(byConsultant).length;
                const formatDate = (dateStr) => {
                  const [year, month, day] = dateStr.split('-');
                  return `${day}-${month}-${year}`;
                };

                return (
                  <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-slate-800">Leave Diary</h3>
                      <button
                        onClick={toggleLeaveCollapsed}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        {leaveCollapsed
                          ? `Expand (${shownLeave.length} ${shownLeave.length === 1 ? 'entry' : 'entries'}, ${peopleCount} ${peopleCount === 1 ? 'person' : 'people'})`
                          : 'Collapse'}
                        <span className="text-xs">{leaveCollapsed ? '▸' : '▾'}</span>
                      </button>
                    </div>
                    {!leaveCollapsed && (
                      <div className="space-y-3">
                        {Object.keys(byConsultant)
                        .sort()
                        .map(consultantName => {
                          const years = byConsultant[consultantName];
                          const totalEntries = Object.values(years).reduce((n, arr) => n + arr.length, 0);

                          return (
                            <div key={consultantName} className="border-l-4 border-blue-500 pl-3">
                              <button
                                onClick={() => toggleConsultantCollapsed(consultantName)}
                                className="w-full flex items-center justify-between text-left font-semibold text-gray-700 mb-1 hover:text-blue-700"
                              >
                                <span>{consultantName}</span>
                                <span className="text-xs font-normal text-gray-500">
                                  {totalEntries} {totalEntries === 1 ? 'entry' : 'entries'}
                                  <span className="ml-1">{collapsedConsultants.has(consultantName) ? '▸' : '▾'}</span>
                                </span>
                              </button>
                              {!collapsedConsultants.has(consultantName) && (
                                <div className="space-y-2">
                                  {Object.keys(years).sort().map(year => {
                                    const yearLeave = years[year]
                                      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
                                    return (
                                      <div key={year}>
                                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{year}</div>
                                        <div className="space-y-1">
                                          {yearLeave.map(l => (
                                            <div key={l.id} className={`flex justify-between items-center text-sm p-2 rounded ${editingLeave?.id === l.id ? 'bg-orange-50 border border-orange-300' : 'bg-gray-50'}`}>
                                              <span>
                                                <strong>{l.reason}</strong> - {formatDate(l.startDate)} to {formatDate(l.endDate)}
                                              </span>
                                              {l.id !== 999999 && (
                                                <div className="flex gap-2 ml-2">
                                                  <button
                                                    onClick={() => startEditLeave(l)}
                                                    className="text-blue-600 hover:text-blue-800 text-xs"
                                                  >
                                                    Edit
                                                  </button>
                                                  <button
                                                    onClick={() => handleRemoveLeave(l.id)}
                                                    className="text-red-600 hover:text-red-800"
                                                  >
                                                    ✕
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ===== LONGER-TERM CALENDAR TAB ===== */}
              {activeTab === 'longterm' && remainingWeeks.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Extended Planning Horizon</h2>
                  <div className="text-sm text-gray-600 mb-2">
                    Weeks {startIndex + 13} onwards ({remainingWeeks.length} weeks)
                  </div>
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed">
                        <thead className="bg-slate-700 text-white">
                          <tr>
                            <th className="w-[18%] px-4 py-3 text-left">Week</th>
                            <th className="w-[10%] px-4 py-3 text-center">Mon</th>
                            <th className="w-[10%] px-4 py-3 text-center">Tue</th>
                            <th className="w-[10%] px-4 py-3 text-center">Wed</th>
                            <th className="w-[10%] px-4 py-3 text-center">Thu</th>
                            <th className="w-[10%] px-4 py-3 text-center">Fri</th>
                            <th className="w-[20%] px-4 py-3 text-left">Weekend On-Call</th>
                            <th className="w-[12%] px-4 py-3 text-left">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {renderWeeksWithYearMarkers(remainingWeeks)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

        {/* Week Detail Modal */}
        <WeekDetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedWeek={selectedWeek}
          weeks={weeks}
          selectedSwapSurgeon1={selectedSwapSurgeon1}
          setSelectedSwapSurgeon1={setSelectedSwapSurgeon1}
          selectedSwapSurgeon2={selectedSwapSurgeon2}
          setSelectedSwapSurgeon2={setSelectedSwapSurgeon2}
          getConsultantNames={getConsultantNames}
          addSwap={addSwap}
          baseStaff={baseStaff}
          onAddAdditionalActivity={handleAddAdditionalActivity}
          onRemoveAdditionalActivity={handleRemoveAdditionalActivity}
          onAddAdditionalPerson={handleAddAdditionalPerson}
          onRemoveAdditionalPerson={handleRemoveAdditionalPerson}
        />
      </div>
    </div>
  );
};

export default TraumaSurgeryPlannerRefactored;